import { EnvironmentProviders, Injectable, inject } from '@angular/core';
import { buildStepToWorkflowMap, provideWorkflowTracking, TrackWorkflowService } from '@aiwyn/shared';
import {
  CreditApplicationEventEventTypeMap,
  CreditApplicationWorkflowEvent,
  CreditApplicationWorkflowEventMetaDataMap,
} from './credit-application-workflow.models';
import { PaymentsWorkflowType } from '../payments-workflow.models';

/**
 * Tracker for Flow 10 (Credit Application). Narrows `TrackWorkflowService` to the
 * credit-application step enum + metadata map and exposes `startOperation` / `endOperation`
 * so the SUBMITTED → SUCCEEDED / FAILED triad can stamp `operationDurationMs`.
 *
 * Root-scoped (`providedIn: 'root'`) because its sole consumer is `PaymentsState.submitCreditAllocations`,
 * which lives in a root-singleton NGXS state and can't host per-container providers. The
 * submit funnel is serialised through the state so a shared operation-timer map is safe.
 */
@Injectable({ providedIn: 'root' })
export class CreditApplicationTrackService {
  private readonly tracker = inject(TrackWorkflowService) as TrackWorkflowService<CreditApplicationWorkflowEvent, CreditApplicationWorkflowEventMetaDataMap>;
  private readonly operationStarts = new Map<string, number>();

  trackWorkflow<E extends CreditApplicationWorkflowEvent>(step: E, metadata?: CreditApplicationWorkflowEventMetaDataMap[E]): void {
    this.tracker.trackWorkflow(step, metadata);
  }

  startOperation(key: string): void {
    this.operationStarts.set(key, performance.now());
  }

  endOperation(key: string): number {
    const start = this.operationStarts.get(key);
    if (start === undefined) {
      return 0;
    }
    this.operationStarts.delete(key);
    return performance.now() - start;
  }
}

export function provideCreditApplicationWorkflowTracking(): EnvironmentProviders {
  return provideWorkflowTracking<CreditApplicationWorkflowEvent, CreditApplicationWorkflowEventMetaDataMap>({
    stepToWorkflow: buildStepToWorkflowMap({ [PaymentsWorkflowType.CREDIT_APPLICATION]: CreditApplicationWorkflowEvent }),
    stepToEventType: CreditApplicationEventEventTypeMap,
  });
}
